import { Location } from "@angular/common";
import { HttpClient, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
    ActivatedRouteSnapshot,
    Resolve,
    RouterStateSnapshot,
} from "@angular/router";
import { OrderItem } from "app/main/modules/operational/reception/os/components/os-items/os-items.component";
import { ApiGateway, PageOptions } from "cw-backend";
import { CwUtils } from "cw-utils";
import { BehaviorSubject, Observable, shareReplay } from "rxjs";
import { ParametersOrderPayPending } from "../../../models/attendance/order-pay-pending";
import { QuotaLackTO } from "../../../models/attendance/QuotaLack";
import { Horary, Performer } from "../../../models/backend-definitions.module";

@Injectable()
export class AttendanceListProvider implements Resolve<any> {
    horary: Horary[];
    attendance: any[];
    orders: [];
    ordersItens: [];
    performer: Performer[];
    attendanceEdit: any;
    onDataChanged: BehaviorSubject<any>;
    onHoraryChanged: BehaviorSubject<any>;
    onTypeChanged: BehaviorSubject<any>;
    onPerformerChanged: BehaviorSubject<any>;
    routeParams: any;
    state: any;
    pageOptions: PageOptions;
    currentPage: PageOptions;

    constructor(
        public gw: ApiGateway,
        private location: Location,
        private http: HttpClient
    ) {
        this.onDataChanged = new BehaviorSubject({});
        this.onPerformerChanged = new BehaviorSubject({});
        this.onTypeChanged = new BehaviorSubject({});
    }

    resolve(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<any> | Promise<any> | any {
        this.routeParams = route.params;

        // Get state for current or new page
        this.state = this.location.getState();
        if (this.state?.currentPage && state.url.includes(this.state?.path)) {
            this.currentPage = this.state?.currentPage;
        }
        this.attendanceEdit = this.state?.currentAttendance;

        if (this.currentPage) {
            this.pageOptions = this.currentPage;
        } else {
            this.pageOptions = new PageOptions();
            this.pageOptions.fieldSort = "person.name";
        }
        return new Promise<void>((resolve, reject) => {
            Promise.all([this.getPage(this.pageOptions, "I")]).then(() => {
                resolve();
            }, reject);
        });
    }

    get(id: number): Observable<HttpResponse<any>> {
        if (!id) {
            return null;
        }
        return this.gw.get("attendance/:id", { id: id });
    }

    getById(id: number): Observable<HttpResponse<any>> {
        if (!id) {
            return null;
        }
        return this.gw.get("attendance/wAll/:id", { id: id });
    }

    getOrderTiss(id: number): Observable<HttpResponse<any>> {
        if (!id) {
            return null;
        }
        return this.gw.get("order/tiss/:id", { id: id });
    }

    checkProductAndFee(id: number): Observable<HttpResponse<Boolean>> {
        if (!id) {
            return null;
        }
        return this.gw.get("order/checkProduct/:id", { id: id });
    }

    getOrderItemListByOrder(orderId:number, billingId?:number) {
        let param = "";
        if(billingId){
            param += `?billingId=${billingId}`
        }
        return new Promise<any>((resolve, reject) => {
            this.gw.get(`order/orderItem/historic/${orderId}${param}`).subscribe({
                next: (res: HttpResponse<any>) => {
                    resolve(res);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    getOrderItemList(orderId) {
        return new Promise<any>((resolve, reject) => {
            this.gw.get(`order/orderItem/list/${orderId}`).subscribe({
                next: (res: HttpResponse<any>) => {
                    resolve(res);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    getAdvancePaymentByOrderId(orderId) {
        return new Promise<any>((resolve, reject) => {
            this.gw.get(`budget/advancePayment/order/${orderId}`).subscribe({
                next: (res: HttpResponse<any>) => {
                    resolve(res);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    getPage(pageOptions: PageOptions, type, refresh?): Promise<any> {
        if (!type) {
            type = "I";
        }
        return new Promise((resolve, reject) => {
            this.gw
                .post("attendance/search/", { type: type }, pageOptions)
                .subscribe({
                    next: (res: HttpResponse<any>) => {
                        if (
                            refresh ||
                            (!CwUtils.compare(
                                this.attendance,
                                res.body.content
                            ) &&
                                res.body.content.length > 0)
                        ) {
                            this.attendance = res.body.content;
                            this.onDataChanged.next(this.attendance);
                        } else {
                            this.attendance = res.body.content;
                        }
                        resolve(res);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getPageOrders(pageOptions: PageOptions, renew?: boolean): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw.post("order/search", pageOptions).subscribe({
                next: (res: HttpResponse<any>) => {
                    if (
                        (!CwUtils.compare(this.orders, res.body.content) &&
                            res.body.content.length > 0) ||
                        renew
                    ) {
                        this.orders = res.body.content;
                        this.onDataChanged.next(this.orders);
                    } else {
                        this.orders = res.body.content;
                    }

                    resolve(res);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    getPageOrdersByPatientId(
        patientId: number,
        pageOptions: PageOptions,
        historic
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw.post(`order/search/${patientId}`, pageOptions).subscribe({
                next: (res: HttpResponse<any>) => {
                    if (!historic) {
                        resolve(res.body.historicPatientTO);
                    } else {
                        resolve(res);
                    }
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    getPageOrdersItems(
        pageOptions: PageOptions,
        id: any,
        renew?: boolean
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw.post(`orderItems/search/${id}`, pageOptions).subscribe({
                next: (res: HttpResponse<any>) => {
                    if (
                        (!CwUtils.compare(this.orders, res.body.content) &&
                            res.body.content.length > 0) ||
                        renew
                    ) {
                        this.ordersItens = res.body.content;
                        this.onDataChanged.next(this.ordersItens);
                    } else {
                        this.ordersItens = res.body.content;
                    }

                    resolve(res);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    getSimplePage(pageOptions: PageOptions, type): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .post("attendance/search/", { type: type }, pageOptions)
                .subscribe({
                    next: (res: HttpResponse<any>) => {
                        resolve(res);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getInsurance(pageOptions: PageOptions): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw.post("attendance/insurance/search", pageOptions).subscribe({
                next: (res: HttpResponse<any>) => {
                    resolve(res);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    getSpecialCovagereList(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw.get("order/special-coverage").subscribe({
                next: (res: HttpResponse<any>) => {
                    resolve(res);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    getProcedureByInsurance(
        insuranceId: number,
        performerId: any,
        companyId: any,
        pageOptions: PageOptions
    ): Promise<any> {
        const body = {
            insuranceId: insuranceId,
            performerId: performerId || "",
            companyId: companyId,
            pageSearchTO: pageOptions,
        };
        return new Promise((resolve, reject) => {
            this.gw
                .post(`attendance/procedure/searchByFilter/`, body)
                .subscribe({
                    next: (res: HttpResponse<any>) => {
                        resolve(res);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getPerformer(
        pageOptions: PageOptions,
        insuranceId: number,
        procedureId?: any
    ): Promise<any> {
        const body = {
            insuranceId: insuranceId,
            procedureId: procedureId || "",
            pageSearchTO: pageOptions,
        };
        return new Promise((resolve, reject) => {
            this.gw.post(`attendance/performer/search`, body)
                .pipe(shareReplay())
                .subscribe({
                    next: (res: HttpResponse<any>) => {
                        resolve(res);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getSpeciality(professionalId, pageOptions: PageOptions): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .post(
                    `attendance/speciality/search/${professionalId}`,
                    pageOptions
                )
                .subscribe({
                    next: (res: HttpResponse<any>) => {
                        resolve(res);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getProfessional(pageOptions: PageOptions): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .post(`attendance/professional/search`, pageOptions)
                .pipe(shareReplay())
                .subscribe({
                    next: (res: HttpResponse<any>) => {
                        resolve(res);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }


    getProfessionalById(professionalId: number): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .get(`attendance/professional/${professionalId}`,)
                .subscribe({
                    next: (res: HttpResponse<any>) => {
                        resolve(res);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getOrders(pageOptions: PageOptions): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw.post(`order/search`, pageOptions).subscribe({
                next: (res: HttpResponse<any>) => {
                    resolve(res);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    getOrderById(id) {
        return new Promise<any>((resolve, reject) => {
            this.gw.get(`order/${id}`).subscribe({
                next: (res: HttpResponse<any>) => {
                    resolve(res);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    getOrderItemById(id) {
        return new Promise<OrderItem>((resolve, reject) => {
            this.gw.get(`order/orderItem/${id}`).subscribe({
                next: (res: HttpResponse<any>) => {
                    resolve(res.body);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    search(filter, entity = "attendance"): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw.post(entity + "/search", filter).subscribe({
                next: (res: HttpResponse<any>) => {
                    resolve(res);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    checkLack(lack: QuotaLackTO) {
        return new Promise((resolve, reject) => {
            this.gw.post(`order/checkLack`, lack).subscribe({
                next: (res: HttpResponse<any>) => {
                    resolve(res);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    getPageSettings() {
        if (this.currentPage) {
            return this.pageOptions;
        } else {
            return;
        }
    }

    getCids(pageOptions: PageOptions): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw.post("cid/search/auto-complete", pageOptions).subscribe({
                next: (res: HttpResponse<any>) => {
                    resolve(res);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    findByOrderAuthorizerStatus(pageOptions: PageOptions): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw.post("order/search/authorizer", pageOptions).subscribe({
                next: (res: HttpResponse<any>) => {
                    resolve(res);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    sendByEmailOrDownload(pageOptions: any, type: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .post(`order/send/email-download/type/${type}`, pageOptions)
                .subscribe({
                    next: (res: HttpResponse<any>) => {
                        resolve(res);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getExameTypeList() {
        return new Promise<any>((resolve, reject) => {
            this.gw.get("attendance/exameType/").subscribe({
                next: (res: HttpResponse<any>) => {
                    resolve(res);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    getMatMed(procedureId: number, insuranceId: number, companyId: number) {
        return new Promise((resolve, reject) => {
            this.gw
                .get(
                    `attendance/accreditation/kit/procedure/${procedureId}/insurance/${insuranceId}/company/${companyId}`
                )
                .subscribe({
                    next: (res: HttpResponse<any>) => {
                        resolve(res);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getViaAccess() {
        return new Promise((resolve, reject) => {
            this.gw.get(`viaAccess/`).subscribe({
                next: (res: HttpResponse<any>) => {
                    resolve(res);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    getViaAdministration() {
        return new Promise((resolve, reject) => {
            this.gw.get(`viaAdministration/`).subscribe({
                next: (res: HttpResponse<any>) => {
                    resolve(res);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    getDefaultValues(patientId) {
        return new Promise((resolve, reject) => {
            this.gw.get(`order/new/${patientId}`).subscribe({
                next: (res) => {
                    resolve(res);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    getSesionByPatientId(patientId, realized, pageOptions) {
        return new Promise<any>((resolve, reject) => {
            this.gw
                .post(`order/session/${patientId}/${realized}`, pageOptions)
                .subscribe({
                    next: (res) => {
                        resolve(res);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    checkOrderSessionToBeCarriedOut(orderId) {
        return new Promise<any>((resolve, reject) => {
            this.gw.get(`order/session/to-be-carried/${orderId}`).subscribe({
                next: (res) => {
                    resolve(res);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    getOrderItemByPatientIdAndSessionId(patientId, sessionId) {
        return new Promise<any>((resolve, reject) => {
            this.gw
                .get(`order/order-item/session/:patientId/:sessionId`, {
                    patientId: patientId,
                    sessionId: sessionId,
                })
                .subscribe({
                    next: (res) => {
                        resolve(res);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getSessionItemBySessionId(sessionId) {
        return new Promise<any>((resolve, reject) => {
            this.gw
                .get(`order/session-item/realized/:sessionId`, {
                    sessionId: sessionId,
                })
                .subscribe({
                    next: (res) => {
                        resolve(res);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getinfoOrderBilling(orderId: number | undefined) {
        return new Promise<any>((resolve, reject) => {
            this.gw
                .get(`order/info-billing/:orderId`, { orderId: orderId })
                .subscribe({
                    next: (res) => {
                        resolve(res);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getMemoryCalc(orderId: number) {
        return new Promise<any>((resolve, reject) => {
            this.gw
                .get(`order/memoryCalc/:orderId`, { orderId: orderId })
                .subscribe({
                    next: (res) => {
                        resolve(res);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getOrdersPayPending(
        parametersOrderPayPending: ParametersOrderPayPending
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .post("order/orderPayPending", parametersOrderPayPending)
                .subscribe({
                    next: (res: HttpResponse<any>) => {
                        resolve(res);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getSummaryOrderByPatient(id) {
        return new Promise<any>((resolve, reject) => {
            this.gw
                .get(
                    `order/summary/${id}`
                )
                .subscribe({
                    next: (res: HttpResponse<any>) => {
                        resolve(res);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getSummaryOrderByPatientAndDate(id, dateStart, dateEnd) {
        return new Promise<any>((resolve, reject) => {
            this.gw
                .get(
                    `order/summary/${id}?dateStart=${dateStart}&dateEnd=${dateEnd}`
                )
                .subscribe({
                    next: (res: HttpResponse<any>) => {
                        resolve(res);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    checkDefaulters() {
        let user = JSON.parse(localStorage.getItem("user"));
        if (!user?.id) {
            return
        }
        return new Promise<any>((resolve, reject) => {
            this.gw
                .get(
                    `order/check-defaulters/${user?.id}`
                )
                .subscribe({
                    next: (res: HttpResponse<any>) => {
                        resolve(res);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }


    confirmdefaulters() {
        return new Promise<any>((resolve, reject) => {
            this.gw
                .post(
                    `order/confirm-defaulters/`
                )
                .subscribe({
                    next: (res: HttpResponse<any>) => {
                        resolve(res);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }
}
